/**
 * What the agent is shown about the connection. **One derivation, two readers.**
 *
 * ┌─ ⛔ ONE CONNECTION STATE, TWO RENDERINGS, AND NOT TWO MECHANISMS ───────────┐
 * │ The banner and the composer's send-disable read THIS, and only this. If     │
 * │ INBOX shipped two independent notions of "connected" they would disagree,   │
 * │ and the disagreement renders as a composer that is enabled underneath a     │
 * │ banner saying nothing is arriving. Recorded as the expensive-to-undo half   │
 * │ of question 4 in `docs/INBOX-WEBSOCKET-DESIGN.md`.                          │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ THE THRESHOLD, AND WHY IT IS A COUNT RATHER THAN A TIMER ──────────────
 *
 * **Armed on the SECOND consecutive failed reconnect.** The quantity that
 * matters is *have we tried and failed*, not *how many seconds have passed*: a
 * first attempt that succeeds is invisible, which is the flicker case, and a
 * second failure means the network is actually gone rather than briefly
 * interrupted.
 *
 * `ops/README.md` §12's argument is why a threshold exists at all: *"a monitor
 * people learn to ignore is worse than no monitor, because it also trains them
 * to ignore the real one later."* ⚠️ **THAT ARGUMENT IS ABOUT FREQUENCY, NOT
 * PERSISTENCE.** It forbids an indicator that fires on a 300 ms reconnect. It
 * does not forbid a banner, and §9.0 rule 4 already ruled the other way for the
 * agent's screen: nine codes render an agent line AND a diagnostic line, **both
 * always visible**. Suppress below the threshold, unmissable above it.
 *
 * ── ⛔ THE WORDING IS THE CONSEQUENCE, NOT THE STATE ─────────────────────────
 *
 * **"Not receiving new messages", never "Disconnected".** An agent cannot act on
 * "disconnected". They can act on "the list in front of you is not updating".
 */
import type { ConnectionState } from './inbox-socket.js'

/**
 * Consecutive failed reconnects before anything is shown.
 *
 * ⚠️ EXPORTED so the test asserts against the same number the code uses. A test
 * with its own literal `2` would keep passing if this moved.
 */
/**
 * ⚠️ THIS COUNTS RECONNECT ATTEMPTS ON A PERSISTENT SOCKET, AND THAT INPUT DOES
 * NOT EXIST ON A PHONE. Recorded as `banner-threshold-assumes-a-socket` in
 * `ops/deferrals.txt`, owner COLLABORATION.
 *
 * A backgrounded mobile app is ALWAYS not receiving, so ported unchanged this
 * threshold arms permanently - violating the exact argument it was derived from,
 * that a monitor people learn to ignore is worse than no monitor. MOBILE is now
 * the next development rather than a future workstream, so the question it needs
 * - "how stale is what I am showing" rather than "is the transport up" - is
 * closer than the derivation assumed.
 *
 * ✅ THE BROWSER BEHAVIOUR IS CORRECT AND UNCHANGED. This is a note about what
 * does not transfer, not a defect in what shipped.
 */
export const ARM_AFTER_CONSECUTIVE_FAILURES = 2

export const NOT_RECEIVING = 'Not receiving new messages'
export const COMPOSER_BLOCKED = 'Not connected. Your draft is kept here and nothing is sent.'

export type ConnectionPresentation = {
  /** The banner's text, or `null` when nothing should be shown. */
  banner: string | null
  /**
   * ⛔ ONE BOOLEAN, NOT A PER-STATE VARIANT. §9.0 rule 4 settled that the
   * composer disable is UNIFORM across the nine error codes rather than varying
   * with the reason, and the same argument applies here: a disable that looked
   * different per connection state would be a second vocabulary the agent has
   * to learn, for a distinction they cannot act on differently.
   */
  composerDisabled: boolean
  /** The single uniform reason line. `null` exactly when the composer is enabled. */
  composerReason: string | null
  /**
   * ┌─ ⛔ THE LIST MUST LOOK STALE, NOT MERELY BE ANNOUNCED AS STALE ────────────┐
   * │ Found by inspection on 2026-08-11. Above the threshold the banner says     │
   * │ "Not receiving new messages" and **the list underneath it looks exactly    │
   * │ like a live one** - crisp rows, fresh-looking timestamps, a green presence  │
   * │ dot. An agent who scrolls past the banner, or returns to a tab they left    │
   * │ open, reads a stale list as current and answers the wrong conversation      │
   * │ first.                                                                     │
   * │                                                                            │
   * │ ⚠️ THIS IS THE SAME BOOLEAN AS `composerDisabled`, DELIBERATELY, and it is │
   * │ a separate FIELD rather than the caller reusing that one. They mean         │
   * │ different things - "the send would probably fail" against "what you are     │
   * │ looking at may be out of date" - and a caller reading `composerDisabled` to │
   * │ dim a list is the line that survives the day they stop coinciding.          │
   * │                                                                            │
   * │ ⛔ NO SECOND MECHANISM. No timer, no `navigator.onLine`, no age-of-last-    │
   * │ message heuristic: the socket's failure count is already the system's one   │
   * │ answer to "are we receiving", and a second one would disagree with it.      │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  listStale: boolean
  /**
   * ⛔ THE RETRY LINE, FROM `nextAttemptInMs` THE SOCKET ALREADY COMPUTES. The
   * banner states a problem; without this it states a problem and no prospect,
   * which is the state that makes people reload the page - and a reload during
   * a backoff is the one action that makes it worse.
   *
   * ⚠️ `null` WHEN THE BANNER IS, so there is never a countdown with nothing
   * above it.
   */
  retryLine: string | null
}

/**
 * ⚠️ ROUNDED UP AND FLOORED AT ONE. `Math.round` renders "Retrying in 0 seconds"
 * for any delay under 500 ms - a promise that has already expired by the time it
 * is painted. A countdown that says zero and keeps sitting there is read as
 * frozen, which is the opposite of the reassurance it exists to give.
 */
function retryIn(ms: number): string {
  return `Retrying in ${Math.max(1, Math.ceil(ms / 1000))} seconds`
}

/**
 * ⚠️ THE COMPOSER AND THE BANNER SHARE THE THRESHOLD, DELIBERATELY.
 *
 * Disabling the composer on the FIRST failure would grey the send button during
 * every 300 ms blip, which is the same flicker the banner's threshold exists to
 * avoid, on the control the agent's hand is already on.
 *
 * ⛔ **AND THE FALSE REFUSAL IS NAMED RATHER THAN HIDDEN.** The send is an HTTP
 * POST, not a socket frame, so a dead socket does not PROVE the send would fail.
 * Blocking it can refuse a send that would have succeeded. That is accepted:
 * above the threshold the network is very likely gone, and question 2's answer
 * is explicit that the agent must be told at the moment they act rather than
 * getting a send that "appears to work and fails later". **The text is always
 * kept**, so the cost of a wrong refusal is a delay and never lost work.
 */
export function presentConnection(state: ConnectionState): ConnectionPresentation {
  const armed = state.kind === 'reconnecting' && state.failures >= ARM_AFTER_CONSECUTIVE_FAILURES

  return armed
    ? {
        banner: NOT_RECEIVING,
        composerDisabled: true,
        composerReason: COMPOSER_BLOCKED,
        listStale: true,
        /**
         * ⚠️ THE NARROWING IS WHAT MAKES THIS SAFE. `nextAttemptInMs` exists only
         * on the `reconnecting` arm, and `armed` already required it - so this
         * reads a field that is present by construction rather than by a default.
         */
        retryLine: retryIn((state as { nextAttemptInMs: number }).nextAttemptInMs),
      }
    : {
        banner: null,
        composerDisabled: false,
        composerReason: null,
        listStale: false,
        retryLine: null,
      }
}
